import { Injectable } from '@angular/core';
import type {
  IdParam,
  JsonArray,
  JsonOrFormBody,
  JsonRecord,
} from './types/api-payloads.types';
import type { ChangePasswordBody, UpdateUserProfileBody } from './types/user-role-payloads.types';
import { CommonPagesCatalogService } from './common-pages-catalog.service';
import { CommonPagesInvestmentService } from './common-pages-investment.service';
import { CommonPagesOperationsService } from './common-pages-operations.service';
import { CommonPagesOrgService } from './common-pages-org.service';
import { CommonPagesPaymentService } from './common-pages-payment.service';
import { CommonPagesUserRoleService } from './common-pages-user-role.service';

/**
 * Application-facing API facade. Implementations live in common-pages-*.service.ts
 * to keep files navigable; callers should keep injecting CommonPagesService only.
 */
@Injectable({
  providedIn: 'root',
})
export class CommonPagesService {
  constructor(
    private _userRole: CommonPagesUserRoleService,
    private _org: CommonPagesOrgService,
    private _catalog: CommonPagesCatalogService,
    private _investment: CommonPagesInvestmentService,
    private _payment: CommonPagesPaymentService,
    private _operations: CommonPagesOperationsService
  ) { }

  getRoles() {
    return this._userRole.getRoles();
  }
  getUserProfileData() {
    return this._userRole.getUserProfileData();
  }

  getInvestmentLiabilitie() {
    return this._org.getInvestmentLiabilitie();
  }

  getOperatorSites() {
    return this._org.getOperatorSites();
  }

  updateProfileData(profileData: JsonRecord) {
    return this._userRole.updateProfileData(profileData);
  }

  checkUniqueUser(userBody: JsonRecord) {
    return this._userRole.checkUniqueUser(userBody);
  }
  getSelectedUserDetails(userId: IdParam) {
    return this._userRole.getSelectedUserDetails(userId);
  }
  getUserDetails(userId: IdParam) {
    return this._userRole.getUserDetails(userId);
  }
  GetSiteByAccount(agencyType = 0) {
    return this._org.GetSiteByAccount(agencyType);
  }
  getAllSuppliersDropdown() {
    return this._org.getAllSuppliersDropdown();
  }
  createUser(userBody: JsonRecord) {
    return this._userRole.createUser(userBody);
  }
  EditUser(userBody: JsonRecord) {
    return this._userRole.EditUser(userBody);
  }
  GetAccountSitesByAgency(userBody: JsonRecord | JsonArray) {
    return this._userRole.GetAccountSitesByAgency(userBody);
  }
  fileUpload(userBody: FormData) {
    return this._userRole.fileUpload(userBody);
  }
  fileDelete(userBody: JsonRecord) {
    return this._userRole.fileDelete(userBody);
  }

  addProgram(programBody: any) {
    return this._catalog.addProgram(programBody);
  }
  updateProgram(programBody: any) {
    return this._catalog.updateProgram(programBody);
  }
  deleteProgram(programId: any) {
    return this._catalog.deleteProgram(programId);
  }
  getAllPrograms(requestBody: any) {
    return this._catalog.getAllPrograms(requestBody);
  }

  makeProgramDefault(formData: any) {
    return this._catalog.makeProgramDefault(formData);
  }
  updateProgramVisible(formData: any) {
    return this._catalog.updateProgramVisible(formData);
  }

  getAllPPartners(requestBody: any) {
    return this._catalog.getAllPPartners(requestBody);
  }
  addPPartners(PPartnerBody: any) {
    return this._catalog.addPPartners(PPartnerBody);
  }
  deletePPartner(partnerId: any) {
    return this._catalog.deletePPartner(partnerId);
  }
  AddPartnerFromDB(formdata: any) {
    return this._catalog.AddPartnerFromDB(formdata);
  }
  searchInDatabasePartner(searchText: any, siteId: any) {
    return this._catalog.searchInDatabasePartner(searchText, siteId);
  }

  GetAllAccountTypesDropdown(accountType: number) {
    return this._org.GetAllAccountTypesDropdown(accountType);
  }

  GetAllSiteTypesDropdown(siteTab: number) {
    return this._org.GetAllSiteTypesDropdown(siteTab);
  }

  addVendors(formdata: any) {
    return this._catalog.addVendors(formdata);
  }
  deletePVendors(vendorId: any) {
    return this._catalog.deletePVendors(vendorId);
  }
  AddVendorFromDB(formdata: any) {
    return this._catalog.AddVendorFromDB(formdata);
  }

  searchInDatabase(searchText: any, siteId: any) {
    return this._catalog.searchInDatabase(searchText, siteId);
  }

  getAllTags(requestBody: any) {
    return this._catalog.getAllTags(requestBody);
  }

  addTagGroup(tagGroupData: any) {
    return this._catalog.addTagGroup(tagGroupData);
  }
  updateTagGroup(tagGroupData: any) {
    return this._catalog.updateTagGroup(tagGroupData);
  }
  deleteTagGroup(groupId: any) {
    return this._catalog.deleteTagGroup(groupId);
  }
  addTag(tagData: any) {
    return this._catalog.addTag(tagData);
  }
  updateTag(tagData: any) {
    return this._catalog.updateTag(tagData);
  }
  deleteTag(groupId: any, tagId: any) {
    return this._catalog.deleteTag(groupId, tagId);
  }

  GetSiteNonAlcBalance(siteId: any) {
    return this._catalog.GetSiteNonAlcBalance(siteId);
  }

  createRole(roleBody: JsonRecord) {
    return this._userRole.createRole(roleBody);
  }
  updateRole(roleBody: JsonRecord) {
    return this._userRole.updateRole(roleBody);
  }
  getRoleById(roleId: string) {
    return this._userRole.getRoleById(roleId);
  }
  getPermissionsByRole(roleName: string) {
    return this._userRole.getPermissionsByRole(roleName);
  }
  updatePermissionsByRole(permissionsBody: JsonRecord) {
    return this._userRole.updatePermissionsByRole(permissionsBody);
  }

  updateEmailDefaultByRole(permissionsBody: JsonRecord) {
    return this._userRole.updateEmailDefaultByRole(permissionsBody);
  }

  createAgency(agencyBody: any) {
    return this._org.createAgency(agencyBody);
  }
  updateAgency(agencyBody: any) {
    return this._org.updateAgency(agencyBody);
  }

  createAccount(accountBody: any) {
    return this._org.createAccount(accountBody);
  }
  updateAccount(accountBody: any) {
    return this._org.updateAccount(accountBody);
  }

  fileContractUpload(userBody: any) {
    return this._org.fileContractUpload(userBody);
  }

  createSite(siteBody: any) {
    return this._org.createSite(siteBody);
  }

  updateSite(siteBody: any) {
    return this._org.updateSite(siteBody);
  }

  updateReps(formData: any) {
    return this._investment.updateReps(formData);
  }

  DeleteInvestment(formData: any) {
    return this._investment.DeleteInvestment(formData);
  }

  getGenericPopulateRecords(type: any) {
    return this._org.getGenericPopulateRecords(type);
  }
  getAllSiteTypes() {
    return this._org.getAllSiteTypes();
  }

  GetInvestment(InvestmentId: any) {
    return this._investment.GetInvestment(InvestmentId);
  }
  GetInvestorBySite(SiteId: any, searchText: string) {
    return this._investment.GetInvestorBySite(SiteId, searchText);
  }
  GetSiteDetails(SiteId: any) {
    return this._investment.GetSiteDetails(SiteId);
  }
  updateInvestment(investmentData: any) {
    return this._investment.updateInvestment(investmentData);
  }
  ApproveInvestment(investmentData: any) {
    return this._investment.ApproveInvestment(investmentData);
  }
  markAsReviewed(investmentId: any) {
    return this._investment.markAsReviewed(investmentId);
  }
  SaveInvestment(investmentData: any) {
    return this._investment.SaveInvestment(investmentData);
  }
  setInvestmentBalanceTransferFlag(body: {
    investmentId: string;
    isBalanceTransfer: boolean;
  }) {
    return this._investment.setInvestmentBalanceTransferFlag(body);
  }
  ScheduleInvestment(investmentData: any) {
    return this._investment.ScheduleInvestment(investmentData);
  }
  GetUserSites(isSupplierInvestment: boolean) {
    return this._investment.GetUserSites(isSupplierInvestment);
  }
  GetInvestorDetail(InvestorId: any, isSupplierInvestment: boolean) {
    return this._investment.GetInvestorDetail(InvestorId, isSupplierInvestment);
  }
  GetInvestorDetailBYQBOID(investorQBOId: any, siteId: any) {
    return this._investment.GetInvestorDetailBYQBOID(investorQBOId, siteId);
  }
  GetInvestorDetailQBO(InvestorId: any) {
    return this._investment.GetInvestorDetailQBO(InvestorId);
  }
  getProgramBySite(siteId: any) {
    return this._investment.getProgramBySite(siteId);
  }

  MapWithExistingCustomer(formData: any) {
    return this._investment.MapWithExistingCustomer(formData);
  }
  MapRetailSupplierToQboCustomer(requestBody: any) {
    return this._investment.MapRetailSupplierToQboCustomer(requestBody);
  }

  AddNewCustomer(customerBody: any) {
    return this._investment.AddNewCustomer(customerBody);
  }
  GetSuppliersList() {
    return this._investment.GetSuppliersList();
  }
  GetRepsName() {
    return this._investment.GetRepsName();
  }
  GetUnmappedQboSuppliers() {
    return this._investment.GetUnmappedQboSuppliers();
  }

  GetPayment(PaymentId: any) {
    return this._payment.GetPayment(PaymentId);
  }
  GetVendor(VendorId: any) {
    return this._payment.GetVendor(VendorId);
  }
  GetSiteVendor(VendorId: any) {
    return this._payment.GetSiteVendor(VendorId);
  }
  MapWithExistingVendor(formData: any) {
    return this._payment.MapWithExistingVendor(formData);
  }
  unlinkVendor(PaymentId: any) {
    return this._payment.unlinkVendor(PaymentId);
  }
  GetQBOVendorDropdownList() {
    return this._payment.GetQBOVendorDropdownList();
  }
  AddNewVendor(vendorBody: any) {
    return this._payment.AddNewVendor(vendorBody);
  }

  CreateBill(formData: any) {
    return this._payment.CreateBill(formData);
  }

  UpdateBill(formData: any) {
    return this._payment.UpdateBill(formData);
  }

  getTagsDropdownList(siteId: any) {
    return this._payment.getTagsDropdownList(siteId);
  }

  billApprove(paymentData: any) {
    return this._payment.billApprove(paymentData);
  }

  updateQBOVendor(vendorData: any) {
    return this._payment.updateQBOVendor(vendorData);
  }
  saveInvestmentLiabilities(liabilities: any) {
    return this._payment.saveInvestmentLiabilities(liabilities);
  }
  deletePayment(paymentId: any) {
    return this._payment.deletePayment(paymentId);
  }

  GetSiteVendorDropdownList(SiteId: any, searchText: string = '') {
    return this._payment.GetSiteVendorDropdownList(SiteId, searchText);
  }

  PreviewQBOAttachment(QBOAttachmentId: any) {
    return this._payment.PreviewQBOAttachment(QBOAttachmentId);
  }

  UploadW9AttachmentFile(formData: any) {
    return this._payment.UploadW9AttachmentFile(formData);
  }

  PaybevBillAttachment(formData: any) {
    return this._payment.PaybevBillAttachment(formData);
  }

  GetExpenseDetail(expenseID: any) {
    return this._payment.GetExpenseDetail(expenseID);
  }

  updateExpense(expenseData: any) {
    return this._payment.updateExpense(expenseData);
  }

  GetCreditDetail(CreditID: any) {
    return this._payment.GetCreditDetail(CreditID);
  }
  GetRefundDetail(refundID: any) {
    return this._payment.GetRefundDetail(refundID);
  }
  updateRefund(refundData: any) {
    return this._payment.updateRefund(refundData);
  }
  GetDepositDetail(DepositID: any) {
    return this._payment.GetDepositDetail(DepositID);
  }
  updateDeposit(DepositData: any) {
    return this._payment.updateDeposit(DepositData);
  }

  updateCredit(CreditData: any) {
    return this._payment.updateCredit(CreditData);
  }

  GetInternalTransferDetails(InternalTransfId: any) {
    return this._payment.GetInternalTransferDetails(InternalTransfId);
  }

  UpdateInternalTransfer(transferData: any) {
    return this._payment.UpdateInternalTransfer(transferData);
  }

  AddOrUpdateAnnouncement(requestBody: any) {
    return this._operations.AddOrUpdateAnnouncement(requestBody);
  }

  changePassword(requestBody: ChangePasswordBody) {
    return this._userRole.changePassword(requestBody);
  }

  AddOrUpdateBAdges(badgeData: any) {
    return this._operations.AddOrUpdateBAdges(badgeData);
  }
  createUnderReview(underReviewData: any) {
    return this._operations.createUnderReview(underReviewData);
  }
  updateUnderReview(underReviewData: any) {
    return this._operations.updateUnderReview(underReviewData);
  }
  updateTransactionUnderReview(underReviewData: any) {
    return this._operations.updateTransactionUnderReview(underReviewData);
  }
  getAllInvestmentUnderReview() {
    return this._operations.getAllInvestmentUnderReview();
  }
  getAllPaymentUnderReview() {
    return this._operations.getAllPaymentUnderReview();
  }
  getEmailInvestmentDetails(investmentId: any, link: any) {
    return this._investment.getEmailInvestmentDetails(investmentId, link);
  }
  getEmailPaymentDetails(investmentId: any, link: any) {
    return this._operations.getEmailPaymentDetails(investmentId, link);
  }
  getEmailExpenseDetails(expenseId: any, link: any) {
    return this._operations.getEmailExpenseDetails(expenseId, link);
  }
  SendInvestmentIssueEmail(emailData: any) {
    return this._investment.SendInvestmentIssueEmail(emailData);
  }
  SendPaymentExpenseIssueEmail(emailData: any) {
    return this._operations.SendPaymentExpenseIssueEmail(emailData);
  }
  AddAccountType(accountType: any) {
    return this._org.AddAccountType(accountType);
  }
  UpdateAccountType(accountType: any) {
    return this._org.UpdateAccountType(accountType);
  }
  AddResouce(resource: any) {
    return this._org.AddResouce(resource);
  }
  UpdateResource(resource: any) {
    return this._org.UpdateResource(resource);
  }

  updateNonAlcDetails(nonAlcDetails: any) {
    return this._org.updateNonAlcDetails(nonAlcDetails);
  }

  getQBOCustomers() {
    return this._org.getQBOCustomers();
  }

  resyncDepositData(depositId: any) {
    return this._payment.resyncDepositData(depositId);
  }
  resyncCreditData(creditId: any) {
    return this._payment.resyncCreditData(creditId);
  }
  resyncExpenseData(expenseId: any) {
    return this._payment.resyncExpenseData(expenseId);
  }
  resyncRefundData(refundId: any) {
    return this._payment.resyncRefundData(refundId);
  }
  resyncInternalTransferData(transferId: any) {
    return this._payment.resyncInternalTransferData(transferId);
  }
  resyncInvestmentData(investmentId: any) {
    return this._investment.resyncInvestmentData(investmentId);
  }
  resyncPaymentData(paymentId: any) {
    return this._payment.resyncPaymentData(paymentId);
  }

  UpdateUserProfile(userBody: JsonOrFormBody | UpdateUserProfileBody) {
    return this._userRole.UpdateUserProfile(userBody);
  }

  manageProfilePicture(formData: FormData) {
    return this._userRole.manageProfilePicture(formData);
  }

  getNonAlcTransactionFilters(siteId: string) {
    return this._org.getNonAlcTransactionFilters(siteId);
  }

  getRetailProgramForSupplier() {
    return this._operations.getRetailProgramForSupplier();
  }
  getRetailSupplierCodes() {
    return this._operations.getRetailSupplierCodes();
  }

  AddRetailPrograms(requestBody: any) {
    return this._operations.AddRetailPrograms(requestBody);
  }
  UpdateRetailPrograms(requestBody: any) {
    return this._operations.UpdateRetailPrograms(requestBody);
  }

  UpdateRetailProgramEmailDefaults(requestBody: any) {
    return this._operations.UpdateRetailProgramEmailDefaults(requestBody);
  }

  exportNonAlcTransactions(
    siteId: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortField: string,
    transactionType: string
  ) {
    return this._org.exportNonAlcTransactions(
      siteId,
      page,
      pageSize,
      sortBy,
      sortField,
      transactionType
    );
  }

  getSupplierById(id: string) {
    return this._org.getSupplierById(id);
  }

  validateToken() {
    return this._userRole.validateToken();
  }

  validateSsoToken(token: string, email: string) {
    return this._userRole.validateSsoToken(token, email);
  }
}
